import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import defaultMaleProfileImg from '../../../assets/images/man_default_img.png';
import defaultFemaleProfileImg from '../../../assets/images/woman_default_img.png';
import {getCoordinatorDetail, updateCoordinator} from "../../../api/coordiApi";
import AreaSelectComponent from '../../../components/common/AreaSelectComponent';
import {defaultAlert} from "../../../utils/alert";

export default function CoordinatorEditPage() {
    const navigate = useNavigate();

    // 기본 정보 (읽기 전용)
    const [memberName, setMemberName] = useState('');
    const [phone, setPhone] = useState('');
    const [category, setCategory] = useState('');
    const [nationality, setNationality] = useState('');
    const [nationalityName, setNationalityName] = useState('');
    const [gender, setGender] = useState('');

    // 수정 가능한 정보
    const [selectedMainArea, setSelectedMainArea] = useState<string>("");
    const [selectedSubArea, setSelectedSubArea] = useState<string>("");

    const [hasCareer, setHasCareer] = useState('no');
    const [careerYear, setCareerYear] = useState('');
    const [careerMonth, setCareerMonth] = useState('');

    const [introduction, setIntroduction] = useState('');
    const [profileImageType, setProfileImageType] = useState('default');
    const [profileImage, setProfileImage] = useState<File | null>(null);
    const [currentImagePath, setCurrentImagePath] = useState('');

    const [certificates, setCertificates] = useState([
        {certificateName: '', certificateIssuer: '', issueDate: ''}
    ]);

    useEffect(() => {
        fetchCoordinatorDetail();
    }, []);

    // 코디네이터 정보 조회
    const fetchCoordinatorDetail = async () => {
        try {
            const res = await getCoordinatorDetail();
            if (res.data?.data) {
                const data = res.data.data;
                // 읽기 전용 정보
                setMemberName(data.memberName);
                setPhone(data.phone);
                setCategory(data.category);
                setNationality(data.nationality);
                setNationalityName(data.nationalityName)
                setGender(data.gender || '');

                // 수정 가능한 정보
                setSelectedMainArea(data.preferredAreaMajor || '');
                setSelectedSubArea(data.preferredAreaMinor || '');
                setHasCareer(data.hasCareer ? 'yes' : 'no');
                setCareerYear(data.careerYear?.toString() || '');
                setCareerMonth(data.careerMonth?.toString() || '');
                setIntroduction(data.introduction || '');
                setProfileImageType(data.imagePath && data.imagePath !== 'default' ? 'upload' : 'default');
                setCurrentImagePath(data.imagePath || '');

                if (data.certificates && data.certificates.length > 0) {
                    setCertificates(data.certificates.map((cert: any) => ({
                        certificateName: cert.certificateName || '',
                        certificateIssuer: cert.certificateIssuer || '',
                        issueDate: cert.issueDate || '',
                    })));
                }
            }
        } catch (error) {
            console.error('Failed to fetch coordinator detail:', error);
            defaultAlert('코디네이터 정보를 불러오는데 실패했습니다.');
        }
    };

    // 자격증 추가
    const handleAddCertificate = () => {
        setCertificates([...certificates, {certificateName: '', certificateIssuer: '',issueDate:''}]);
    };

    // 자격증 삭제
    const handleRemoveCertificate = (index: number) => {
        if (certificates.length > 1) {
            setCertificates(certificates.filter((_, i) => i !== index));
        }
    };

    // 자격증 변경
    const handleCertificateChange = (index: number, field: 'certificateName' | 'certificateIssuer' | 'issueDate', value: string) => {
        if (field === 'issueDate') {
            value = value.replace(/[^0-9]/g, '').slice(0, 8);
        }
        const newCertificates = [...certificates];
        newCertificates[index][field] = value;
        setCertificates(newCertificates);
    };

    // 발행일자 유효성 검사 (yyyyMMdd)
    const isValidIssueDate = (dateStr: string): boolean => {
        if (dateStr.length !== 8) return false;
        const year = parseInt(dateStr.substring(0, 4));
        const month = parseInt(dateStr.substring(4, 6));
        const day = parseInt(dateStr.substring(6, 8));
        if (year < 1900 || year > new Date().getFullYear()) return false;
        if (month < 1 || month > 12) return false;
        const lastDay = new Date(year, month, 0).getDate();
        if (day < 1 || day > lastDay) return false;
        return true;
    };

    // 이미지 업로드
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setProfileImage(e.target.files[0]);
        }
    };

    // 카테고리 텍스트
    const getCategoryText = (category: string) => {
        return category === 'GENERAL' ? '일반 간병인' : '요양보호사';
    };

    // 저장
    const handleSubmit = async () => {
        if (!selectedMainArea || !selectedSubArea) {
            defaultAlert('선호 지역을 선택해주세요.');
            return;
        }
        if (hasCareer === 'yes' && (!careerYear || !careerMonth)) {
            defaultAlert('경력 정보를 입력해주세요.');
            return;
        }
        if (!introduction.trim()) {
            defaultAlert('코디 소개를 작성해주세요.');
            return;
        }

        const validCertificates = certificates.filter(
            cert => cert.certificateName.trim() !== '' || cert.certificateIssuer.trim() !== '' || cert.issueDate.trim() !== ''
        );

        if (validCertificates.some(cert => !cert.certificateName.trim() || !cert.certificateIssuer.trim() || !cert.issueDate.trim())) {
            defaultAlert('자격증명과 발행일자 및 발행기관을 모두 입력해주세요.');
            return;
        }

        if (validCertificates.some(cert => !isValidIssueDate(cert.issueDate))) {
            defaultAlert('발행일자를 올바른 형식(yyyyMMdd)으로 입력해주세요.\n예: 20240315');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('category', category);
            formData.append('nationality', nationality);
            formData.append('preferredAreaMajor', selectedMainArea);
            formData.append('preferredAreaMinor', selectedSubArea);
            formData.append('hasCareer', String(hasCareer === 'yes'));
            if (hasCareer === 'yes' && careerYear) formData.append('careerYear', careerYear);
            if (hasCareer === 'yes' && careerMonth) formData.append('careerMonth', careerMonth);
            formData.append('introduction', introduction);
            formData.append('certificates', JSON.stringify(validCertificates));

            if (profileImageType === 'upload' && profileImage) {
                formData.append('imageFile', profileImage);
            } else if (profileImageType === 'upload' && currentImagePath) {
                formData.append('imagePath', currentImagePath);
            } else {
                formData.append('imagePath', 'default');
            }

            const response = await updateCoordinator(formData);
            if (response.data) {
                defaultAlert('정보가 수정되었습니다.');
                navigate('/coordi/detail');
            }
        } catch (error) {
            console.error('Failed to update coordinator:', error);
            defaultAlert('정보 수정에 실패했습니다.');
        }
    };
    const getByteLength = (str: string) => {
        return new Blob([str]).size;
    };

    return (
        <div>
            {/* 하단 버튼 */}
            <div className="bottom-btn">
                <button type="button" className="btn confirm" onClick={handleSubmit}>
                    저장
                </button>
            </div>

            {/* 컨테이너 */}
            <main className="container type_gray">
                <form className="form-box">
                    {/* 코디 정보 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>코디 구분</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">코디 정보</span>
                                </h3>
                                <div className="round-box mt10">
                                    <div className="round-body">
                                        <div className="info-row">
                                            <span className="label">구분</span>
                                            <span className="cont">{getCategoryText(category)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </section>

                    {/* 기본 정보 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>기본 정보</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">기본 정보</span>
                                </h3>
                                <div className="round-box mt10">
                                    <div className="round-body">
                                        <div className="info-row">
                                            <span className="label">이름</span>
                                            <span className="cont">{memberName}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">연락처</span>
                                            <span className="cont">{phone}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">국적</span>
                                            <span className="cont">{nationalityName}</span>
                                        </div>

                                        <div className="info-row">
                                            <span className="label">선호 지역</span>
                                            <span className="cont">
                                            <AreaSelectComponent
                                                selectedMainArea={selectedMainArea}
                                                selectedSubArea={selectedSubArea}
                                                onMainAreaChange={setSelectedMainArea}
                                                onSubAreaChange={setSelectedSubArea}
                                                required={true}
                                            />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </section>

                    {/* 경력 정보 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>경력 정보</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">경력 정보</span>
                                </h3>
                                <div className="form-item">
                                    <div className="radio-row">
                                        <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio3"
                                                name="career"
                                                value="no"
                                                checked={hasCareer === 'no'}
                                                onChange={(e) => setHasCareer(e.target.value)}
                                            />
                                            <label htmlFor="radio3">미경력</label>
                                        </span>
                                        <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio4"
                                                name="career"
                                                value="yes"
                                                checked={hasCareer === 'yes'}
                                                onChange={(e) => setHasCareer(e.target.value)}
                                            />
                                            <label htmlFor="radio4">경력 있음</label>
                                        </span>
                                    </div>
                                    {hasCareer === 'yes' && (
                                        <div className="round-box mt10 bg-gray">
                                            <div className="comb-row">
                                                <select
                                                    value={careerYear}
                                                    onChange={(e) => setCareerYear(e.target.value)}
                                                >
                                                    <option value="" hidden>년</option>
                                                    {[...Array(20)].map((_, i) => (
                                                        <option key={i} value={i}>
                                                            {i}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="unit">년</span>
                                                <select
                                                    value={careerMonth}
                                                    onChange={(e) => setCareerMonth(e.target.value)}
                                                >
                                                    <option value="" hidden>개월</option>
                                                    {[...Array(12)].map((_, i) => (
                                                        <option key={i} value={i}>
                                                            {i}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="unit">개월</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </fieldset>
                    </section>

                    {/* 코디 소개 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>코디 소개</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">코디 소개</span>
                                </h3>
                                <div className="form-item">
                                    <div className="input-box">
                                        <textarea
                                            className="com"
                                            placeholder="코디 소개글을 작성해 주세요."
                                            value={introduction}
                                            onChange={(e) => setIntroduction(e.target.value)}
                                            maxLength={300}
                                        />
                                        <span className="unit">
                                            <strong className="point">{getByteLength(introduction)}</strong>/300 byte
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </section>

                    {/* 프로필 이미지 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>프로필 이미지</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">프로필 이미지</span>
                                </h3>
                                <div className="form-item">
                                    <div className="radio-row">
                                        <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio5"
                                                name="profileImg"
                                                value="default"
                                                checked={profileImageType === 'default'}
                                                onChange={(e) => setProfileImageType(e.target.value)}
                                            />
                                            <label htmlFor="radio5">기본 이미지 사용</label>
                                        </span>
                                        <span className="text-radio">
                                            <input
                                                type="radio"
                                                id="radio6"
                                                name="profileImg"
                                                value="upload"
                                                checked={profileImageType === 'upload'}
                                                onChange={(e) => setProfileImageType(e.target.value)}
                                            />
                                            <label htmlFor="radio6">이미지 업로드</label>
                                        </span>
                                    </div>
                                    <div className="thumbnail-wrap profileImg">
                                        <div className="thumbnail">
                                            <div className="centered">
                                                <img
                                                    src={
                                                        profileImageType === 'default'
                                                            ? (gender === 'M' || gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg)
                                                            : profileImage
                                                                ? URL.createObjectURL(profileImage)
                                                                : (!currentImagePath || currentImagePath === 'default')
                                                                    ? (gender === 'M' || gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg)
                                                                    : currentImagePath
                                                    }
                                                    className="landscape"
                                                    alt="프로필"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = gender === 'M' || gender === '남성' ? defaultMaleProfileImg : defaultFemaleProfileImg;
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {profileImageType === 'upload' && (
                                            <div className="img-file">
                                                <div className="file-upload">
                                                    <input
                                                        type="file"
                                                        id="ex_filename1"
                                                        className="upload-hidden"
                                                        onChange={handleImageUpload}
                                                        accept="image/*"
                                                    />
                                                </div>
                                                <label htmlFor="ex_filename1" className="inp-btn">
                                                    <i className="fa-regular fa-image"></i>
                                                    <span className="blind">상품 이미지 등록</span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </section>

                    {/* 자격증 */}
                    <section className="bg-white">
                        <fieldset>
                            <legend>자격증</legend>
                            <div className="inner-box">
                                <h3 className="cont-tit">
                                    <span className="underline">자격증</span>
                                </h3>
                                <div className="round-box mt10">
                                    <div className="round-body">
                                        {certificates.map((cert, index) => (
                                            <div key={index} className="form-item">
                                                <label className="input-tit">
                                                    자격증명
                                                    {index === 0 && (
                                                        <button
                                                            type="button"
                                                            className="add-btn"
                                                            onClick={handleAddCertificate}
                                                        >
            <span>
                <i className="fa-solid fa-plus"></i>
            </span>
                                                            추가
                                                        </button>
                                                    )}
                                                </label>
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        placeholder="자격증명을 입력해주세요."
                                                        value={cert.certificateName}
                                                        onChange={(e) =>
                                                            handleCertificateChange(index, 'certificateName', e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        placeholder="발행일자 (yyyyMMdd)"
                                                        value={cert.issueDate}
                                                        maxLength={8}
                                                        inputMode="numeric"
                                                        onChange={(e) =>
                                                            handleCertificateChange(index, 'issueDate', e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        placeholder="발급기관을 입력해주세요."
                                                        value={cert.certificateIssuer}
                                                        onChange={(e) =>
                                                            handleCertificateChange(index, 'certificateIssuer', e.target.value)
                                                        }
                                                    />
                                                </div>
                                                {index > 0 && (
                                                    <button
                                                        type="button"
                                                        className="remove-btn"
                                                        onClick={() => handleRemoveCertificate(index)}
                                                    >
        <span>
            <i className="fa-solid fa-minus"></i>
        </span>
                                                        삭제
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </section>
                </form>
            </main>
        </div>
    );
}